/*
  deadline-products.js — 受注締切が近い順に商品を並べる共通部品（8月施策 A4「締切間近面」）

  使い方:
    DeadlineProducts.mount(rootElement, {
      token:      Storefront APIトークン（必須）
      mode:       'shelf'（TOP棚: 上限件数まで1本の横並び）| 'page'（専用ページ: 締切日ごとの見出し付き縦積み）
      limit:      shelf時の上限（既定 12）
      withinDays: shelf時に対象とする残日数（既定 7。0以下=無制限）
      refBlock:   経由売上/計測の枠名
      onCount(n): 対象件数が確定したとき（ページの「N 商品」表示用）
      onReady/onEmpty
    });

  スケール上の注意（2026-08-20時点の設計判断）:
    - products(query:) は metafield を条件にできないため「全公開商品を取得→JS側で締切判定」方式。
      上限は無い（hasNextPage が尽きるまでページング）が、リクエスト数は商品数に比例する
      （現在約1,000件=5リクエスト・初回1〜2秒・以後10分キャッシュ）。
    - 公開商品が3,000件を超えたら乗り換えを検討すること:
      a) Storefront search + productFilters(productMetafield) … S&Dアプリで sales_end_date の
         フィルター登録が必要。完全一致のみなので「今日〜+N日」を日付列挙でOR指定する
      b) 日次タグ付け（Admin API）+ 自動コレクション … 規模無制限・サーバー描画

  仕様:
    - 全公開商品を Storefront API で取得（250件×ページング）し、custom.sales_end_date が今日以降のものだけ残す
      → 締切昇順（同日は publishedAt 降順）。取得結果は sessionStorage に10分キャッシュ（再訪・タブ切替で再取得しない）
    - 販売中(availableForSale)・販売開始済み(release_date が今日以前)のみ
    - 商品カードは /products/<handle>?view=card-item（テーマ共通。締切バッジ sales-deadline-label はカード側で描画される）
    - JST基準（締切=その日の23:59:59）。残日数の計算は snippets/sales-deadline-label.liquid と同じ
    - 計測: root に data-ref-block を付与（attribution-ref.js / block-tracking.js が拾う）
*/
(function () {
  var DEFAULTS = {
    endpoint: '/api/2024-07/graphql.json',
    mode: 'shelf',
    limit: 12,
    withinDays: 7,
    refBlock: 'deadline_shelf',
    cacheKey: 'aniclo_deadline_products_v2',
    cacheTtlMs: 10 * 60 * 1000,
    timeoutMs: 10000,
    gridClasses: ''
  };

  var QUERY = [
    'query DeadlineProducts($cursor:String) {',
    '  products(first:250, after:$cursor, query:"available_for_sale:true", sortKey:CREATED_AT, reverse:true) {',
    '    pageInfo { hasNextPage endCursor }',
    '    nodes {',
    '      handle title publishedAt availableForSale vendor productType',
    '      release: metafield(namespace:"custom", key:"release_date") { value }',
    '      salesEnd: metafield(namespace:"custom", key:"sales_end_date") { value }',
    '      ordertype: metafield(namespace:"custom", key:"item_ordertype") { value }',
    '      series: metafield(namespace:"custom", key:"brand_series") { value }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // --- JST 日付ユーティリティ ---
  function jstYmd(d) {
    var parts = new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
    var g = function (t) { return parts.find(function (p) { return p.type === t; }).value; };
    return g('year') + '-' + g('month') + '-' + g('day');
  }
  function ymdOf(val) {
    // "2026-08-20" / "2026-08-20T00:00:00Z" のどちらでも日付部分だけ取る（Shopifyのdate型は日付文字列）
    var s = String(val || '');
    var m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    try { return jstYmd(new Date(s)); } catch (e) { return ''; }
  }
  function daysLeft(endYmd, todayYmd) {
    // 締切日までの残り日数（今日=0, 明日=1 …）
    var a = new Date(endYmd + 'T00:00:00+09:00').getTime();
    var b = new Date(todayYmd + 'T00:00:00+09:00').getTime();
    return Math.round((a - b) / 86400000);
  }

  function fetchWithTimeout(url, opt, ms) {
    var c = new AbortController();
    var t = setTimeout(function () { c.abort(); }, ms);
    return fetch(url, Object.assign({}, opt || {}, { signal: c.signal })).finally(function () { clearTimeout(t); });
  }

  // --- 全商品取得（キャッシュ付き） ---
  function readCache(key, ttl) {
    try {
      var r = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (r && r.ts && (Date.now() - r.ts) < ttl && Array.isArray(r.items)) return r.items;
    } catch (e) {}
    return null;
  }
  function writeCache(key, items) {
    try { sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), items: items })); } catch (e) {}
  }

  function fetchAll(opt) {
    var cached = readCache(opt.cacheKey, opt.cacheTtlMs);
    if (cached) return Promise.resolve(cached);

    var items = [];
    function page(cursor) {
      return fetchWithTimeout(opt.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': opt.token },
        body: JSON.stringify({ query: QUERY, variables: { cursor: cursor } }),
        credentials: 'same-origin'
      }, opt.timeoutMs)
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (j) {
          var p = j.data && j.data.products;
          if (!p) throw new Error('no data');
          p.nodes.forEach(function (n) {
            if (!n || !n.handle || !n.salesEnd || !n.salesEnd.value) return;
            items.push({
              handle: n.handle,
              title: n.title,
              publishedAt: n.publishedAt || '',
              available: !!n.availableForSale,
              release: n.release && n.release.value ? ymdOf(n.release.value) : '',
              end: ymdOf(n.salesEnd.value),
              // フィルター用（新着一覧＝コレクションページと同じ4軸）
              vendor: n.vendor || '',
              productType: n.productType || '',
              ordertype: n.ordertype && n.ordertype.value ? String(n.ordertype.value) : '',
              series: n.series && n.series.value ? String(n.series.value) : ''
            });
          });
          if (p.pageInfo.hasNextPage) return page(p.pageInfo.endCursor);
        });
    }
    return page(null).then(function () { writeCache(opt.cacheKey, items); return items; });
  }

  // --- 絞り込み・並び替え ---
  function selectItems(items, opt) {
    var today = jstYmd(new Date());
    var out = [];
    items.forEach(function (it) {
      if (!it.available || !it.end) return;
      var dl = daysLeft(it.end, today);
      if (dl < 0) return;                                  // 締切済み
      if (it.release && it.release > today) return;        // 販売開始前
      if (opt.mode === 'shelf' && opt.withinDays > 0 && dl > opt.withinDays) return;
      out.push(Object.assign({}, it, { daysLeft: dl }));
    });
    out.sort(function (a, b) {
      if (a.end !== b.end) return a.end < b.end ? -1 : 1;
      return (b.publishedAt || '').localeCompare(a.publishedAt || '');
    });
    return out;
  }

  // --- フィルター（コレクションページと同じ4軸） ---
  var FACETS = [
    { key: 'ordertype',   label: '商品タイプ' },
    { key: 'vendor',      label: '作品' },
    { key: 'series',      label: 'シリーズ' },
    { key: 'productType', label: 'カテゴリー' }
  ];
  function applyFilters(list, filters) {
    if (!filters) return list;
    return list.filter(function (it) {
      return FACETS.every(function (f) {
        var sel = filters[f.key];
        if (!sel || !sel.length) return true;
        return sel.indexOf(it[f.key]) !== -1;
      });
    });
  }
  // 各ファセットの選択肢と件数（他ファセットの選択を反映＝コレクションページと同じ挙動）
  function facetCounts(list, filters) {
    var out = {};
    FACETS.forEach(function (f) {
      var others = Object.assign({}, filters || {}); delete others[f.key];
      var base = applyFilters(list, others);
      var counts = {};
      base.forEach(function (it) { var v = it[f.key]; if (!v) return; counts[v] = (counts[v] || 0) + 1; });
      out[f.key] = Object.keys(counts).sort(function (a, b) { return a.localeCompare(b, 'ja'); })
        .map(function (v) { return { value: v, count: counts[v] }; });
    });
    return out;
  }

  // --- カード描画 ---
  function fetchCards(list, opt) {
    return Promise.all(list.map(function (p) {
      return fetchWithTimeout('/products/' + p.handle + '?view=card-item', { credentials: 'same-origin' }, opt.timeoutMs)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    })).then(function (htmls) {
      var frag = document.createDocumentFragment();
      htmls.filter(Boolean).forEach(function (html) {
        var box = document.createElement('div');
        box.innerHTML = html;
        var item = box.querySelector('.product-item');
        if (item && opt.gridClasses) opt.gridClasses.split(/\s+/).forEach(function (c) { if (c) item.classList.add(c); });
        Array.prototype.slice.call(box.childNodes).forEach(function (n) { if (n.nodeType === 1) frag.appendChild(n); });
      });
      return frag;
    });
  }

  function fmtYmd(ymd) {
    var m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return ymd;
    // JSTの曜日: 日付をUTC正午として作れば getUTCDay がその日の曜日になる
    var wd = ['日', '月', '火', '水', '木', '金', '土'][new Date(ymd + 'T12:00:00Z').getUTCDay()];
    return parseInt(m[2], 10) + '月' + parseInt(m[3], 10) + '日（' + wd + '）';
  }

  function renderShelf(root, list, opt) {
    root.innerHTML = '';
    var wrap = document.createElement('div');
    wrap.className = 'scroller';
    wrap.innerHTML = '<div class="scroller__inner"><div class="product-list product-list--vertical product-list--scrollable dlp__list"></div></div>';
    root.appendChild(wrap);
    var listEl = wrap.querySelector('.dlp__list');
    return fetchCards(list.slice(0, opt.limit), opt).then(function (frag) {
      if (!frag.childNodes.length) return false;
      listEl.appendChild(frag);
      return true;
    });
  }

  // --- ページ用: 1ページ分の描画（締切日ごとの区切り見出し付き） ---
  function renderPageSlice(root, slice, opt) {
    root.innerHTML = '';
    var groups = []; var byEnd = {};
    slice.forEach(function (it) {
      if (!byEnd[it.end]) { byEnd[it.end] = { end: it.end, daysLeft: it.daysLeft, items: [] }; groups.push(byEnd[it.end]); }
      byEnd[it.end].items.push(it);
    });
    var chain = Promise.resolve(false);
    groups.forEach(function (g) {
      chain = chain.then(function (any) {
        var sec = document.createElement('section');
        var urgent = g.daysLeft <= 7;
        sec.className = 'dlp__group' + (urgent ? ' dlp__group--urgent' : '');
        var badge = g.daysLeft === 0 ? '本日締切' : ('あと' + g.daysLeft + '日');
        sec.innerHTML =
          '<header class="dlp__group-head">' +
            '<span class="dlp__group-badge' + (urgent ? ' is-urgent' : '') + '">' + esc(badge) + '</span>' +
            '<h2 class="dlp__group-title heading h4">' + esc(fmtYmd(g.end)) + ' 23:59 受注締切</h2>' +
            '<span class="dlp__group-count">' + g.items.length + '点</span>' +
          '</header>' +
          '<div class="product-list product-list--collection dlp__grid"></div>';
        root.appendChild(sec);
        return fetchCards(g.items, opt).then(function (frag) {
          if (!frag.childNodes.length) { sec.remove(); return any; }
          sec.querySelector('.dlp__grid').appendChild(frag);
          return true;
        });
      });
    });
    return chain;
  }

  function mount(root, options) {
    if (!root) return null;
    var opt = Object.assign({}, DEFAULTS, options || {});
    if (!opt.token) return null;
    root.classList.add('dlp');
    root.setAttribute('data-ref-block', opt.refBlock);

    function done(ok) {
      if (ok) { if (typeof opt.onReady === 'function') opt.onReady(); }
      else if (typeof opt.onEmpty === 'function') opt.onEmpty();
    }

    // ページモードの状態（フィルター/ページ番号/表示件数）。呼び出し側がツールバー/サイドバーを描く
    var state = { all: [], filters: {}, page: 1, perPage: opt.perPage || 48 };
    var api = {
      getState: function () { return state; },
      getFacets: function () { return facetCounts(state.all, state.filters); },
      filtered: function () { return applyFilters(state.all, state.filters); },
      setFilters: function (f) { state.filters = f || {}; state.page = 1; return api.render(); },
      setPage: function (p) { state.page = Math.max(1, p | 0); return api.render(); },
      setPerPage: function (n) { state.perPage = Math.max(1, n | 0); state.page = 1; return api.render(); },
      render: function () {
        var list = api.filtered();
        var total = list.length;
        var pages = Math.max(1, Math.ceil(total / state.perPage));
        if (state.page > pages) state.page = pages;
        var start = (state.page - 1) * state.perPage;
        var slice = list.slice(start, start + state.perPage);
        if (typeof opt.onPage === 'function') {
          try {
            opt.onPage({ total: total, page: state.page, pages: pages, from: total ? start + 1 : 0, to: start + slice.length,
              perPage: state.perPage, facets: api.getFacets(), filters: state.filters });
          } catch (e) {}
        }
        if (!slice.length) { root.innerHTML = ''; return Promise.resolve(false); }
        return renderPageSlice(root, slice, opt).then(function (ok) {
          if (typeof opt.onRendered === 'function') { try { opt.onRendered(); } catch (e) {} }
          return ok;
        });
      }
    };

    fetchAll(opt)
      .then(function (items) {
        var list = selectItems(items, opt);
        if (typeof opt.onCount === 'function') { try { opt.onCount(list.length); } catch (e) {} }
        if (!list.length) { root.innerHTML = ''; done(false); return; }
        if (opt.mode === 'page') {
          state.all = list;
          return api.render().then(function () { done(true); });
        }
        return renderShelf(root, list, opt).then(function (ok) { done(!!ok); });
      })
      .catch(function (e) {
        try { console.error('[DLP] failed', e); } catch (_) {}
        root.innerHTML = '';
        done(false);
      });

    return api;
  }

  window.DeadlineProducts = { mount: mount, FACETS: FACETS };
})();
